import type { App } from "vue";
import * as Sentry from "@sentry/vue";
import environment from "@/environment";
import { isApiError, isApiErrorHandled } from "@/services/api";

declare const __APP_VERSION__: string;

// HTTP statuses que representam fluxo esperado e não-acionável (auth expirada,
// rate-limit, quota/billing). O app já trata esses (toast/redirect), então no
// Sentry seriam só ruído. Todos os demais — incluindo 400/404/409/422 e 5xx —
// são mantidos: um desses *unhandled* costuma ser bug real do client.
const IGNORED_API_STATUS = new Set([401, 403, 429, 402]);

export function configSentry(app: App) {
  const dsn = environment.VITE_SENTRY_DSN;
  const rawEnv = environment.VITE_SENTRY_ENVIRONMENT;
  // Se o placeholder de runtime não foi substituído (misconfig de deploy), fail-safe.
  const env = rawEnv.includes("__") ? "unknown" : rawEnv;
  const force = environment.VITE_SENTRY_FORCE === "true";

  // Local (vite dev) nunca reporta, a não ser que forçado para teste.
  // Builds (staging/prod) reportam assim que o DSN real é injetado em runtime.
  const enabled =
    force || (!import.meta.env.DEV && env !== "local" && !!dsn && !dsn.includes("__"));

  if (!enabled) return;

  Sentry.init({
    app,
    dsn,
    environment: env,
    release: __APP_VERSION__,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay desativado por ora: plano grátis, replay consome cota rápido
      // e o vídeo do feedback acaba filmando o próprio form (pouco útil). Pode voltar
      // depois (com sampling baixo) se a cota permitir. Sem replay, o feedback do
      // AI-978 ainda envia identidade + rota; só não anexa gravação.
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1,
    tracePropagationTargets: [/^https:\/\/api(\.dev)?\.sippulse\.ai/, "localhost"],
    // Ruído: erros de extensão/3rd-party/rede e chunk-load que o app já recupera
    // (o reload de chunk vive em router/index.ts).
    ignoreErrors: [
      /Failed to fetch/i,
      /Load failed/,
      /NetworkError/i,
      /AbortError/,
      /dynamically imported module/,
      /Importing a module script failed/,
      "ChunkLoadError",
      /ResizeObserver loop/,
    ],
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-(web-)?extension:\/\//,
      /googletagmanager\.com/,
      /connect\.facebook\.net/,
    ],
    beforeSend(event, hint) {
      // DOM Event capturado como exceção (ex.: <audio>/<img> que falhou ao
      // carregar, rejeição com o próprio Event): o Sentry sintetiza um título
      // ilegível ("Event captured as exception"). Reescreve pra algo triável
      // com o alvo do evento; não dropa — pode ser sinal real (mídia/SSE).
      if (hint.originalException instanceof globalThis.Event) {
        const domEvent = hint.originalException;
        const target = domEvent.target;
        const targetDesc =
          target instanceof HTMLElement
            ? `<${target.tagName.toLowerCase()}> ${(target as HTMLMediaElement).src ?? ""}`.trim()
            : (target?.constructor.name ?? "unknown");
        const exception = event.exception?.values?.[0];
        if (exception) {
          exception.type = "DomEventError";
          exception.value = `DOM Event (type=${domEvent.type}) — target: ${targetDesc}`;
        }
        event.tags = { ...event.tags, dom_event: domEvent.type };
        return event;
      }

      if (isApiError(hint.originalException)) {
        const error = hint.originalException;
        const status = error.response?.status;

        // ApiError já tratado/exibido ao usuário (toast via notifyError) que
        // escapou como unhandled rejection: ruído, não bug. Garantia robusta
        // do AI-977, independente da ordem dos listeners de unhandledrejection.
        if (isApiErrorHandled(error)) {
          return null;
        }

        // Descarta respostas de API esperadas e já tratadas pelo app.
        if (status && IGNORED_API_STATUS.has(status)) {
          return null;
        }

        event.contexts = {
          ...event.contexts,
          api: {
            url: error.config?.url,
            method: error.config?.method,
            status,
            response_data: error.response?.data,
          },
        };
        if (status) {
          event.tags = { ...event.tags, "api.status": status };
        }
      }

      return event;
    },
  });
}
